import { Button, Modal, Spinner, Typography } from "@/components/atoms";

interface ResendConfirmationModalProps {
  open: boolean;
  /** Display id shown in the copy, e.g. "J000123". */
  displayJobId: string;
  /** Address the confirmation went to. Absent only on a row whose list payload carried none. */
  recipient?: string;
  onClose: () => void;
  onConfirm: () => void;
  loading?: boolean;
}

/**
 * Confirmation gate for re-sending a booking's confirmation email.
 *
 * There is nothing to fill in — the destination is fixed to the address the confirmation already went
 * to, and is shown rather than chosen. The modal exists only so the sender can see WHERE it is going
 * before committing, which matters most for an assistant or operator acting on a client's booking.
 *
 * Every booking list now carries that address, so this opens with no request behind it. It is display
 * only regardless: the server resolves the real recipient from the booking, so a row that somehow
 * arrived without one still sends.
 */
export const ResendConfirmationModal = ({
  open,
  displayJobId,
  recipient,
  onClose,
  onConfirm,
  loading = false,
}: ResendConfirmationModalProps) => (
  <Modal
    open={open}
    onClose={loading ? () => undefined : onClose}
    variant="web-small"
    className="sm:w-[440px]"
    title={`Resend confirmation for ${displayJobId}?`}
    description="We'll send this booking's confirmation again, to the same address it went to originally."
    renderButtons={() => (
      <>
        <Button variant="tertiary" size="lg" onClick={onClose} disabled={loading} className="flex-1">
          Cancel
        </Button>
        <Button
          variant="primary"
          size="lg"
          onClick={onConfirm}
          disabled={loading}
          className="flex-1"
          startIcon={loading ? <Spinner variant="light" /> : undefined}
        >
          Resend
        </Button>
      </>
    )}
  >
    <div className="rounded-lg border border-neutral-mid-gray bg-neutral-surface-gray p-3">
      <Typography variant="small" className="mb-1 block">
        Send to
      </Typography>
      <Typography variant="small-regular" className="text-neutral-dark-gray">
        {/* The server resolves the recipient either way; this is display only, so an address missing
            from the list says so rather than blocking a send that would succeed. */}
        {recipient ?? "the address on this booking"}
      </Typography>
    </div>
  </Modal>
);
